import React from "react"

/**
 * FAQ items as visible open Q&A using <dl>/<dt>/<dd>.
 * All content is in the DOM at page load (no accordion / JS toggle) so crawlers can index answers.
 * FAQPage JSON-LD is not emitted for these FAQ bands on directory or marketing surfaces.
 *
 * Props:
 *   faqs — array of { q: string, a: string | ReactNode }
 *   embedded — when true, omit outer spacing and card shell so the parent can
 *     set max-width and padding (e.g. About page inside a shared content column).
 */
const FaqSection = ({ faqs, embedded = false }) => {
  if (!faqs || faqs.length === 0) return null

  const inner = (
    <>
      <div className="max-w-2xl">
        <p className="section-eyebrow">Common questions</p>
        <h2
          id="faq-heading"
          className="mt-2 text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl"
        >
          Frequently asked questions
        </h2>
        <p className="mt-3 text-base text-gray-500">
          Something else on your mind?{" "}
          <a
            href="mailto:contact@thehiveindex.com"
            className="text-rose-600 font-semibold hover:underline"
          >
            Get in touch
          </a>
          .
        </p>
      </div>

      <dl className="mt-10 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2">
        {faqs.map((item) => (
          <div key={item.q}>
            <dt className="text-base font-semibold text-gray-900 leading-snug">
              {item.q}
            </dt>
            <dd className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
              {item.a}
            </dd>
          </div>
        ))}
      </dl>
    </>
  )

  if (embedded) {
    return (
      <section className="pt-10 pb-2 sm:pt-12 sm:pb-4" aria-labelledby="faq-heading">
        {inner}
      </section>
    )
  }

  return (
    <section
      className="my-14 sm:my-16 lg:my-20 reveal"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-5xl mx-auto bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden px-6 py-10 sm:px-10 sm:py-12">
        {inner}
      </div>
    </section>
  )
}

export default FaqSection
